import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SharedReviewScreen, { SharedReviewAnswer } from './admin/SharedReviewScreen';
import config from './config';

interface ExamAttemptQuestionResult {
  questionId: number;
  questionOrder: number;
  questionText: string;
  questionLatex?: string | null;
  selectedOption?: string | null;
  correctOption?: string | null;
  isCorrect: boolean;
  marksAwarded: number;
  maxMarks: number;
  explanation?: string | null;
  teacherComment?: string | null;
}

interface ExamAttemptResult {
  id: number;
  examId: number;
  examName: string;
  attemptNumber: number;
  status: string;
  timeSpentSeconds: number;
  totalQuestions: number;
  attemptedQuestions: number;
  correctCount: number;
  incorrectCount: number;
  rawScore: number;
  totalMarks: number;
  percentage: number;
  isPassed: boolean;
  startedAt: string;
  completedAt?: string | null;
  payloadVersion: number;
  attemptSnapshotJson?: string | null;
  detailedResults: ExamAttemptQuestionResult[];
}

interface SnapshotPayload {
  attemptId: number;
  examId: number;
  examName?: string;
  answers?: ExamAttemptQuestionResult[];
}

const mapAttemptToReviewAnswers = (result: ExamAttemptResult): SharedReviewAnswer[] =>
  result.detailedResults.map((question) => ({
    questionId: question.questionId,
    questionText: question.questionText,
    studentAnswerDisplay: question.selectedOption || 'Not answered',
    correctAnswerDisplay: question.correctOption || 'N/A',
    options: [],
    marks: question.maxMarks,
    isCorrect: question.isCorrect,
    teacherComment: question.teacherComment || '',
    marksAwarded: question.marksAwarded,
    status: question.isCorrect ? 'Correct' : 'Incorrect',
  }));

export const ExamAttemptReview: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const [result, setResult] = useState<ExamAttemptResult | null>(location.state?.result || null);
  const [loading, setLoading] = useState(!result);
  const [error, setError] = useState<string | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewNotice, setReviewNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [reviewAnswers, setReviewAnswers] = useState<SharedReviewAnswer[]>([]);

  useEffect(() => {
    if ((location.state as { fromAdmin?: boolean } | null)?.fromAdmin && attemptId) {
      navigate(`/admin/submissions/${attemptId}`, { replace: true, state: { fromAdmin: true } });
      return;
    }
  }, [attemptId, location.state, navigate]);

  useEffect(() => {
    if (!result) {
      return;
    }

    setReviewAnswers(mapAttemptToReviewAnswers(result));
    setReviewComment('');
  }, [result]);

  useEffect(() => {
    const fetchAttempt = async () => {
      if (result || !attemptId) {
        return;
      }

      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/examattempts/${attemptId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch exam attempt');
        }

        const data: ExamAttemptResult = await response.json();
        setResult(data);
      } catch (fetchError) {
        console.error(fetchError);
        setError('Failed to load exam attempt review');
      } finally {
        setLoading(false);
      }
    };

    fetchAttempt();
  }, [attemptId, result, getAccessTokenSilently]);

  const snapshotStatus = useMemo(() => {
    if (!result?.attemptSnapshotJson) {
      return { valid: false, reason: 'Snapshot JSON not available' };
    }

    try {
      const parsed = JSON.parse(result.attemptSnapshotJson) as SnapshotPayload;
      const hasAnswers = Array.isArray(parsed.answers) && parsed.answers.length > 0;
      const hasRows = result.detailedResults.length > 0;

      if (hasAnswers && hasRows) {
        return { valid: true, reason: 'Snapshot JSON and answer rows are available' };
      }

      return { valid: false, reason: 'Snapshot or answer rows are incomplete' };
    } catch {
      return { valid: false, reason: 'Snapshot JSON is invalid' };
    }
  }, [result]);

  const handleQuestionCommentChange = (questionId: number, value: string) => {
    setReviewAnswers((prev) => prev.map((answer) => answer.questionId === questionId ? { ...answer, teacherComment: value } : answer));
  };

  const handleQuestionCorrectChange = (questionId: number, isCorrect: boolean) => {
    setReviewAnswers((prev) => prev.map((answer) => answer.questionId === questionId ? { ...answer, isCorrect, marksAwarded: isCorrect ? answer.marks : 0 } : answer));
  };

  const handleQuestionMarksChange = (questionId: number, marks: number) => {
    setReviewAnswers((prev) => prev.map((answer) => answer.questionId === questionId ? { ...answer, marksAwarded: marks } : answer));
  };

  const handleSaveReview = async () => {
    if (!attemptId) return;
    try {
      setReviewSaving(true);
      setReviewNotice(null);
      const token = await getAccessTokenSilently();
      const payload = {
        teacherComment: reviewComment,
        answers: reviewAnswers.map((answer) => ({
          questionId: answer.questionId,
          isCorrect: answer.isCorrect,
          marksAwarded: answer.marksAwarded ?? answer.marks,
          teacherComment: answer.teacherComment || undefined,
        })),
      };
      const response = await fetch(`${config.apiBaseUrl}/api/examattempts/${attemptId}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to save review');
      setReviewNotice({ type: 'success', message: 'Review saved successfully.' });
    } catch (error) {
      console.error(error);
      setReviewNotice({ type: 'error', message: 'Failed to save review. Please try again.' });
    } finally {
      setReviewSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-lg">
          <div className="text-lg font-semibold text-red-700">{error || 'Attempt not found'}</div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className={`rounded-3xl p-6 text-white shadow-xl sm:p-8 ${result.isPassed ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-red-600 to-rose-600'}`}>
          <button
            onClick={() => navigate(`/exam-center/${result.examId}`)}
            className="mb-4 rounded-lg border border-white/40 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white/90 hover:bg-white/20 hover:text-white"
          >
            Back to Exam Details
          </button>

          <h1 className="text-3xl font-bold">{result.examName}</h1>
          <p className="mt-2 text-sm text-white/90">Attempt #{result.attemptNumber} review</p>

          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
            <div className="rounded-2xl bg-white/15 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Score</p>
              <p className="mt-2 text-2xl font-bold">{result.percentage.toFixed(1)}%</p>
            </div>
            <div className="rounded-2xl bg-white/15 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Correct</p>
              <p className="mt-2 text-2xl font-bold">{result.correctCount}</p>
            </div>
            <div className="rounded-2xl bg-white/15 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Incorrect</p>
              <p className="mt-2 text-2xl font-bold">{result.incorrectCount}</p>
            </div>
            <div className="rounded-2xl bg-white/15 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Attempted</p>
              <p className="mt-2 text-2xl font-bold">{result.attemptedQuestions}/{result.totalQuestions}</p>
            </div>
            <div className="rounded-2xl bg-white/15 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Time</p>
              <p className="mt-2 text-2xl font-bold">{Math.floor(result.timeSpentSeconds / 60)}m</p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
          <h2 className="text-2xl font-bold text-slate-900">Attempt Integrity</h2>
          <div className={`mt-4 rounded-2xl border p-4 ${snapshotStatus.valid ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
            <p className={`text-sm font-semibold ${snapshotStatus.valid ? 'text-green-800' : 'text-amber-800'}`}>
              {snapshotStatus.valid ? 'Snapshot and answer rows validated' : 'Using available attempt data'}
            </p>
            <p className={`mt-1 text-sm ${snapshotStatus.valid ? 'text-green-700' : 'text-amber-700'}`}>
              {snapshotStatus.reason}
            </p>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
          <SharedReviewScreen
            studentName={result.examName}
            studentEmail=""
            attemptNumber={result.attemptNumber}
            status={result.status}
            startedAt={result.startedAt}
            submittedAt={result.completedAt || undefined}
            answers={reviewAnswers}
            comment={reviewComment}
            saving={reviewSaving}
            notice={reviewNotice}
            onCommentChange={setReviewComment}
            onQuestionCommentChange={handleQuestionCommentChange}
            onQuestionCorrectChange={handleQuestionCorrectChange}
            onQuestionMarksChange={handleQuestionMarksChange}
            onSave={handleSaveReview}
            onBack={() => navigate(`/exam-center/${result.examId}`)}
            showReviewControls
          />
        </div>
      </div>
    </div>
  );
};

export default ExamAttemptReview;
